import { H5, H6 } from "@sellar-io/components";
import {
  DeliveryChargeFulfillmentType,
  FulfillmentTypeListResponseType,
  SupplierRelationshipFulfillmentType,
  SupplierGetResponseType,
} from "@sellar-io/api-v2/types";
import {
  comparatorNameToComparator,
  formatCurrency,
  generateFulfillmentTypeContainerDeliveryChargeName,
  pluralize,
} from "@sellar-io/common";
import dayjs from "dayjs";
import { useState } from "react";
import { useMediaQuery } from "usehooks-ts";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../../../components/modal/Dialog";
import { ContextOrderBasketType, useSellarUser } from "../../../../../hooks";
import { useDeliveryAreaList } from "../../../../../hooks/api/v2/deliveryArea/useDeliveryAreaList";
import { useFulfillmentTypeList } from "../../../../../hooks/api/v2/fulfillmentType/useFulfillmentTypeList";

import { AlertContainer } from "../../../../../components/alert/AlertContainer";
import { capitalize } from "../../../../../utils/nativeLodash";
import { FulfillmentTypeRadioSelector } from "../../../Components/FulfillmentTypeRadioSelector";
import { useStockListContext } from "../../../useStockListContext";
import { DeliveryMethodRadioSelector } from "../../../Components/DeliveryMethodRadioSelector";
import { CheckoutSelectedFulfillmentType } from "../../..";
import { useStorefrontBuyerPostcode } from "../lib/useStorefrontBuyerPostcode";

type InitialFulfillmentTypeType = SupplierRelationshipFulfillmentType;
const renderWeekCadence = (cadence: number | null) => {
  if (cadence === null) return null;
  if (cadence < 2) return null;
  return cadence;
};

const canFulfillmentTypeHaveCharges = (
  fulfillmentType: CheckoutSelectedFulfillmentType,
): fulfillmentType is DeliveryChargeFulfillmentType => {
  return ["direct", "courier", "haulier"].includes(fulfillmentType ?? "");
};

export const StorefrontDeliveryOptionsDialog = ({
  active,
  onDismiss,
  ...props
}: {
  active: boolean;
  onDismiss: (fulfillmentType: CheckoutSelectedFulfillmentType | null) => void;
} & DeliveryOptionsDialogContentType) => {
  const [fulfillmentType, setFulfillmentType] = useState<
    CheckoutSelectedFulfillmentType | null | undefined
  >(props.fulfillmentType);
  const [previousFulfillmentType, setPreviousFulfillmentType] = useState(
    props.fulfillmentType,
  );
  if (props.fulfillmentType !== previousFulfillmentType) {
    setPreviousFulfillmentType(props.fulfillmentType);
    setFulfillmentType(props.fulfillmentType);
  }

  const closeDialog = () => {
    if (!fulfillmentType) return onDismiss(null);
    // set updated fulfillmentType for storefront when we close dialog
    onDismiss(fulfillmentType);
  };

  return (
    <Dialog active={active} onDismiss={closeDialog} fullHeight={true}>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={closeDialog}
        className={"z-10 pb-0"}
        title={`${props.supplier.Business.name}'s delivery information`}
      />
      <DialogContent>
        {!!fulfillmentType && (
          <StorefrontDeliveryOptionsDialogContent
            {...props}
            fulfillmentType={fulfillmentType}
            setFulfillmentType={setFulfillmentType}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export type DeliveryOptionsDialogContentType = {
  fulfillmentType: CheckoutSelectedFulfillmentType;
  supplier: SupplierGetResponseType;
  connection:
    | { fulfillmentType: SupplierRelationshipFulfillmentType | null }
    | undefined
    | null;
  unAuthorisedUserPostcode: string | null;
  order: ContextOrderBasketType | null | undefined;
};

export const StorefrontDeliveryOptionsDialogContent = ({
  supplier,
  fulfillmentType,
  connection,
  unAuthorisedUserPostcode,
  setFulfillmentType,
  order,
}: DeliveryOptionsDialogContentType & {
  setFulfillmentType: (fulfillmentType: InitialFulfillmentTypeType) => void;
}) => {
  const isDesktop = useMediaQuery("(min-width: 1280px)");
  const { user, loading } = useSellarUser();
  const { supplierStorefront } = useStockListContext();
  const { buyerPostcode } = useStorefrontBuyerPostcode();
  const deliveryNotes =
    supplierStorefront?.deliveryZoneNotes ??
    (fulfillmentType === "direct"
      ? supplier.directDeliveryNotes
      : supplier.deliveryNotes);

  const isUnauthUserWithPostcode =
    !user && !loading && !!unAuthorisedUserPostcode;
  const isUser = !!user;

  const deliveryAreaEnabled = isUnauthUserWithPostcode || isUser;
  const deliveryAreaQuery = useDeliveryAreaList(
    {
      query: [
        {
          type: "SupplierId" as const,
          value: supplier.id,
          modifier: "EQUALS" as const,
        },
        ...(unAuthorisedUserPostcode
          ? [
              {
                type: "RetailerPostcode" as const,
                value: unAuthorisedUserPostcode,
                modifier: "EQUALS" as const,
              },
            ]
          : []),
      ],
      sortBy: "createdAt",
      sortDir: "desc",
    },
    {
      enabled: deliveryAreaEnabled,
    },
  );

  const fulfillmentTypeQuery = useFulfillmentTypeList(
    {
      query: [
        {
          type: "SupplierId",
          value: supplier.id,
          modifier: "EQUALS",
        },
        ...(canFulfillmentTypeHaveCharges(fulfillmentType)
          ? [
              {
                type: "FulfillmentType",
                value: fulfillmentType,
                modifier: "EQUALS",
              } as const,
            ]
          : []),
      ],
    },
    {
      enabled: !!fulfillmentType && fulfillmentType !== "collection",
    },
  );

  const deliveryAreas = deliveryAreaQuery.data?.pages[0];

  const fulfillmentTypeData = fulfillmentTypeQuery.data?.pages[0]?.[0];
  const changeFulfillmentType = (
    fulfillmentType: CheckoutSelectedFulfillmentType,
  ) => {
    if (!fulfillmentType) return;
    setFulfillmentType(fulfillmentType);
  };

  const hasDeliveryMethods = !!supplierStorefront?.deliveryMethods;

  return (
    <div className="flex flex-col gap-2">
      {/* NOTE: buyer can't choose different fulfillment type for card payment with deposit */}
      {!isDesktop && !supplier.onlyAllowCardPaymentWithDeposit && (
        <>
          {hasDeliveryMethods ? (
            <DeliveryMethodRadioSelector
              connection={connection}
              onUpdate={(value) => changeFulfillmentType(value)}
              supplier={supplier}
              value={fulfillmentType}
              order={order}
              supplierStorefront={supplierStorefront}
              buyerPostcode={buyerPostcode}
            />
          ) : (
            <FulfillmentTypeRadioSelector
              connection={connection}
              onUpdate={(value) => changeFulfillmentType(value)}
              supplier={supplier}
              value={fulfillmentType ?? undefined}
              order={order}
              supplierStorefront={supplierStorefront}
              buyerPostcode={buyerPostcode}
            />
          )}
        </>
      )}
      {!!deliveryAreas?.length && fulfillmentType === "direct" && (
        <>
          <H5>Direct delivery schedule for your venue</H5>
          {deliveryAreas.map((deliveryArea) => (
            <div
              key={deliveryArea.id}
              className={"divide-y rounded-lg border border-solid"}
            >
              <div className="grid grid-cols-2 p-4">
                <div className="col-span-1 grid grid-cols-2">
                  <H6 className={"col-span-1"}>Post codes</H6>
                  <p className={"col-span-1"}>
                    {deliveryArea.DeliveryAreaPostcodes.map(
                      (deliveryAreaPostcode) =>
                        deliveryAreaPostcode.postcodeComponent,
                    ).join(", ")}
                  </p>
                </div>
              </div>
              {!!deliveryArea.DeliveryAreaDays.length && (
                <div className="grid grid-cols-2 p-4">
                  <div className="col-span-1 grid grid-cols-2">
                    <H6 className={"col-span-1"}>Deliveries</H6>
                    <p className={"col-span-1"}>
                      {[
                        "Every",
                        renderWeekCadence(deliveryArea.weeklyCadence),
                        pluralize("week", deliveryArea.weeklyCadence ?? 0),
                        "on",
                      ]
                        .filter(Boolean)
                        .join(" ")}{" "}
                      {deliveryArea.DeliveryAreaDays.map((deliveryAreaDay) =>
                        capitalize(deliveryAreaDay.day).substring(0, 3),
                      ).join(", ")}
                    </p>
                  </div>
                </div>
              )}
              {deliveryArea.orderCutOffDaysBefore &&
                deliveryArea.orderCutOffTime && (
                  <div className="grid grid-cols-2 p-4">
                    <div className="col-span-1 grid grid-cols-2">
                      <H6 className={"col-span-1"}>Order cut-off</H6>
                      <p className={"col-span-1"}>
                        {deliveryArea.orderCutOffDaysBefore} day(s) prior before{" "}
                        {dayjs(deliveryArea.orderCutOffTime).get("hours") - 1 <
                        1
                          ? "23"
                          : dayjs(deliveryArea.orderCutOffTime).get("hours") -
                            1}
                        :
                        {dayjs(deliveryArea.orderCutOffTime).get("minutes") ===
                        0
                          ? "00"
                          : dayjs(deliveryArea.orderCutOffTime).get("minutes")}
                      </p>
                    </div>
                  </div>
                )}
            </div>
          ))}
        </>
      )}

      {fulfillmentType === "courier" && (
        <div className="rounded-lg border border-solid p-4">
          <div className="flex flex-col gap-2">
            <H5>Delivery charges</H5>
            <div className={"flex flex-col gap-3"}>
              {!!fulfillmentTypeData?.deliveryChargeCap && (
                <AlertContainer
                  className={"mt-1 w-full p-3 text-blue-800"}
                  type={"information"}
                >
                  <div className="flex flex-col gap-1">
                    <div className="flex flex-row justify-between">
                      <p className={"font-semibold"}>Max. charge per order</p>
                      <p className={"font-semibold"}>
                        {formatCurrency(fulfillmentTypeData.deliveryChargeCap)}
                      </p>
                    </div>
                    <p>
                      For large orders, delivery charges are capped and your
                      supplier may deliver your order via pallet
                    </p>
                  </div>
                </AlertContainer>
              )}
              {!!fulfillmentTypeData?.charge && (
                <div className={"flex flex-row justify-between"}>
                  <p>Flat charge per order</p>
                  <p>{formatCurrency(fulfillmentTypeData.charge)}</p>
                </div>
              )}
              {!!fulfillmentTypeData?.FulfillmentTypeContainerDeliveryCharges
                .length && (
                <div className={"flex flex-col"}>
                  <H5>Per item charges</H5>
                  <>
                    {fulfillmentTypeData.FulfillmentTypeContainerDeliveryCharges.map(
                      (fulfillmentTypeContainerDeliveryCharge) => (
                        <PerItemCharge
                          fulfillmentTypeContainerDeliveryCharge={
                            fulfillmentTypeContainerDeliveryCharge
                          }
                          key={fulfillmentTypeContainerDeliveryCharge.id}
                        />
                      ),
                    )}
                  </>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {(fulfillmentTypeData?.DeliveryChargeRules ?? []).map((rule, i) => (
        <div key={i} className="rounded-lg border border-solid p-4">
          <div className="flex flex-col gap-2">
            <H5>Free delivery</H5>
            <p>
              {rule.deliveryCharge == 0
                ? "Free"
                : formatCurrency(rule.deliveryCharge, supplier.currency)}{" "}
              delivery on {rule.col}{" "}
              {comparatorNameToComparator(rule.comparator) == ">="
                ? "over"
                : ""}{" "}
              {formatCurrency(rule.value, supplier.currency)}
            </p>
          </div>
        </div>
      ))}
      {deliveryNotes &&
        ["direct", "courier", "haulier"].includes(fulfillmentType ?? "") && (
          <div className="rounded-lg border border-solid p-4">
            <div className="flex flex-col gap-2">
              <H5>
                {supplier.Business.name}'s{" "}
                {fulfillmentType === "direct" ? "local" : "nationwide"} delivery
                notes
              </H5>
              <p>{deliveryNotes}</p>
            </div>
          </div>
        )}
    </div>
  );
};

const PerItemCharge = ({
  fulfillmentTypeContainerDeliveryCharge,
}: {
  fulfillmentTypeContainerDeliveryCharge: FulfillmentTypeListResponseType[number]["FulfillmentTypeContainerDeliveryCharges"][number];
}) => {
  const containerDeliveryChargeName =
    generateFulfillmentTypeContainerDeliveryChargeName({
      volume: fulfillmentTypeContainerDeliveryCharge.volume,
      packQuantity: fulfillmentTypeContainerDeliveryCharge.packQuantity,
      displayUnit: fulfillmentTypeContainerDeliveryCharge.displayUnit,
      containerType: fulfillmentTypeContainerDeliveryCharge.containerType,
    });
  return (
    <div className={"flex flex-row justify-between"}>
      <p>{containerDeliveryChargeName}</p>
      <p>{formatCurrency(fulfillmentTypeContainerDeliveryCharge.charge)}</p>
    </div>
  );
};
